import { useState } from 'react';
import { Building2, Users, Landmark, Split, X } from 'lucide-react';
import type { StripePayoutPreference } from '../../lib/stripePayoutPreference';

interface StripePayoutAccountChoiceModalProps {
  teamName: string;
  organizationName: string;
  onChoose: (preference: StripePayoutPreference) => void;
  onClose: () => void;
}

export function StripePayoutAccountChoiceModal({
  teamName,
  organizationName,
  onChoose,
  onClose,
}: StripePayoutAccountChoiceModalProps) {
  const [loading, setLoading] = useState<StripePayoutPreference | null>(null);

  const handleChoose = (preference: StripePayoutPreference) => {
    setLoading(preference);
    onChoose(preference);
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />

      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-slate-700 my-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-8 pb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Same bank account for team and events?
          </h2>
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-5">
            You manage both <span className="font-medium text-gray-900 dark:text-white">{teamName}</span> and{' '}
            <span className="font-medium text-gray-900 dark:text-white">{organizationName}</span>.
            For accounting and taxes, tell us whether payouts should go to one account or two.
          </p>

          <div className="space-y-3">
            <button
              type="button"
              disabled={!!loading}
              onClick={() => handleChoose('shared')}
              className="w-full text-left p-4 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-teal-500 dark:hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-500/10 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                  <Landmark className="w-5 h-5 text-teal-700 dark:text-teal-300" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Yes — same payout account</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                    Team fees, store sales, and tournament registration fees all deposit to one Stripe account and bank account.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              disabled={!!loading}
              onClick={() => handleChoose('separate')}
              className="w-full text-left p-4 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-50/50 dark:hover:bg-purple-500/10 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Split className="w-5 h-5 text-purple-700 dark:text-purple-300" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">No — separate accounts</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                    Connect Stripe separately for your team and your event organization, each with its own bank account.
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 dark:text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Team
            </span>
            <span className="inline-flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> Events
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
